import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MonitorIcon from '@mui/icons-material/Monitor';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

interface AdPreviewData {
  title: string;
  text?: string;
  imageUrl?: string;
  ctaText?: string;
  targetUrl?: string;
}

interface Props {
  data: AdPreviewData;
}

const WebPreview = ({ data }: Props) => (
  <Box
    sx={{
      position: 'relative',
      borderRadius: 3,
      overflow: 'hidden',
      minHeight: 260,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      bgcolor: data.imageUrl ? undefined : '#e5e7eb',
    }}
  >
    {/* The creative as an <img>, not a CSS background. The URL is advertiser-supplied: the
        write boundary now constrains it, but a CSS url() sink is one stray character away
        from a stylesheet injection, and an image element is governed by img-src instead. */}
    {data.imageUrl && (
      <Box
        component="img"
        src={data.imageUrl}
        alt=""
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    )}
    {/* Dark overlay */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background:
          'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.1) 100%)',
      }}
    />
    {/* Sponsored label */}
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        left: 12,
        bgcolor: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        borderRadius: '12px',
        px: 1.5,
        py: 0.3,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 10, fontWeight: 500 }}
      >
        Sponsored
      </Typography>
    </Box>
    {/* Content */}
    <Box sx={{ position: 'relative', p: 3, textAlign: 'center' }}>
      <Typography
        variant="h6"
        sx={{ color: '#fff', fontWeight: 700, mb: 0.5, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
      >
        {data.title || 'Ad Title'}
      </Typography>
      {data.text && (
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 1.5 }}>
          {data.text}
        </Typography>
      )}
      {data.ctaText && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: '#f59e0b',
            color: '#fff',
            borderRadius: '20px',
            px: 2.5,
            py: 1,
            fontWeight: 600,
            fontSize: 13,
            boxShadow: '0 4px 12px rgba(245,158,11,0.3)',
          }}
        >
          {data.ctaText}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Box>
      )}
    </Box>
  </Box>
);

const MobilePreview = ({ data }: Props) => (
  <Box
    sx={{
      maxWidth: 340,
      mx: 'auto',
      bgcolor: '#fff',
      borderRadius: 3,
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
    }}
  >
    {/* Sponsored header */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>Ad</Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111' }}>Sponsored</Typography>
        <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>Promoted</Typography>
      </Box>
    </Box>

    {/* Image */}
    {data.imageUrl ? (
      <Box
        component="img"
        src={data.imageUrl}
        alt={data.title}
        sx={{ width: '100%', height: 180, objectFit: 'cover' }}
      />
    ) : (
      <Box
        sx={{
          width: '100%',
          height: 180,
          bgcolor: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ color: '#9ca3af', fontSize: 13 }}>No image</Typography>
      </Box>
    )}

    {/* Content */}
    <Box sx={{ p: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#111', mb: 0.5 }}>
        {data.title || 'Ad Title'}
      </Typography>
      {data.text && (
        <Typography sx={{ fontSize: 13, color: '#6b7280', mb: 1.5, lineHeight: 1.4 }}>
          {data.text}
        </Typography>
      )}
      {data.ctaText && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            bgcolor: '#4f46e5',
            color: '#fff',
            borderRadius: 2.5,
            py: 1.2,
            px: 2,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {data.ctaText}
          <ArrowForwardIcon sx={{ fontSize: 14 }} />
        </Box>
      )}
    </Box>
  </Box>
);

export default function AdPreviewPanel({ data }: Props) {
  const [mode, setMode] = useState<'web' | 'mobile'>('web');

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
          Ad Preview
        </Typography>
        <ToggleButtonGroup value={mode} exclusive onChange={(_, v) => v && setMode(v)} size="small">
          <ToggleButton value="web" sx={{ px: 1.5, py: 0.3 }}>
            <MonitorIcon sx={{ fontSize: 16, mr: 0.5 }} /> Web
          </ToggleButton>
          <ToggleButton value="mobile" sx={{ px: 1.5, py: 0.3 }}>
            <PhoneIphoneIcon sx={{ fontSize: 16, mr: 0.5 }} /> Mobile
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ bgcolor: '#f9fafb', borderRadius: 2, p: 2 }}>
        {mode === 'web' ? <WebPreview data={data} /> : <MobilePreview data={data} />}
      </Box>
      {data.targetUrl && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 1, color: '#9ca3af', wordBreak: 'break-all' }}
        >
          Links to: {data.targetUrl}
        </Typography>
      )}
    </Card>
  );
}
